// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import { environment } from "environment/environment";
import {
  getOrCreateUserSchedulerSessionId,
  getUserSchedulerSessionId,
  heartbeatUserSchedulerSession,
  stopUserSchedulerSession,
} from "../../service/api/UserSchedulerPresenceApi";

const AuthContext = createContext();
const MAX_TIMER_DELAY_MS = 2_147_483_647;

const parsePlanEndDate = (value) => {
  if (typeof value !== "string") return null;

  const match = value.match(/^(\d{2})-(\d{2})-(\d{4})(?: (\d{2}):(\d{2}):(\d{2}))?$/);
  if (!match) return null;

  const [, day, month, year, hours = "00", minutes = "00", seconds = "00"] = match;
  const parsed = new Date(
    `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+05:30`
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isPlanActive = (plan) => {
  if (!plan || plan.activePlan === false) return false;

  const endDate = parsePlanEndDate(plan.subscribeDateEnd);
  if (endDate) return endDate.getTime() > Date.now();

  // Plans returned by the updated API always include activePlan and an end date.
  return plan.activePlan === true;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlanLoading, setIsPlanLoading] = useState(true);

  const updateUserPlan = useCallback((planData) => {
    try {
      const activePlan = isPlanActive(planData) ? planData : null;
      setUserPlan(activePlan);

      if (activePlan) {
        sessionStorage.setItem("userPlan", JSON.stringify(activePlan));
      } else {
        sessionStorage.removeItem("userPlan");
      }
    } catch (error) {
      console.error("Plan update error:", error);
    }
  }, []);

  const refreshUserPlan = useCallback(async (userId) => {
    if (!userId) {
      updateUserPlan(null);
      return null;
    }

    setIsPlanLoading(true);
    try {
      const response = await fetch(
        `${environment.baseUrl}/user-pan/get-user-plain-by-user-id?userId=${encodeURIComponent(userId)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) throw new Error(`Plan request failed: ${response.status}`);

      const json = await response.json();
      const plan = json.status && json.data && isPlanActive(json.data) ? json.data : null;
      updateUserPlan(plan);
      return plan;
    } catch (error) {
      console.warn("Unable to refresh user plan:", error);
      // Fail closed so a stale cached plan cannot grant paid feature access.
      updateUserPlan(null);
      return null;
    } finally {
      setIsPlanLoading(false);
    }
  }, [updateUserPlan]);

  // ✅ Load from sessionStorage (auto cleared on tab close)
  useEffect(() => {
    let hasStoredUser = false;
    try {
      const storedUser = sessionStorage.getItem("userInfo");
      const storedPlan = sessionStorage.getItem("userPlan");

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.accessToken) {
          hasStoredUser = true;
          setUser(parsedUser);
        } else {
          sessionStorage.removeItem("userInfo");
          sessionStorage.removeItem("userPlan");
        }
      }
      if (storedPlan) updateUserPlan(JSON.parse(storedPlan));
    } catch (err) {
      console.error("Error parsing session data:", err);
      sessionStorage.clear();
    } finally {
      setIsLoading(false);
      if (!hasStoredUser) setIsPlanLoading(false);
    }
  }, [updateUserPlan]);

  useEffect(() => {
    if (!user?.userId) {
      setIsPlanLoading(false);
      return undefined;
    }

    refreshUserPlan(user.userId);
    return undefined;
  }, [user?.userId, refreshUserPlan]);

  useEffect(() => {
    const endDate = parsePlanEndDate(userPlan?.subscribeDateEnd);
    if (!endDate) return undefined;

    let expiryTimer;
    const checkExpiry = () => {
      const millisecondsUntilExpiry = endDate.getTime() - Date.now();
      if (millisecondsUntilExpiry <= 0) {
        updateUserPlan(null);
        return;
      }

      // Browser setTimeout uses a signed 32-bit delay (about 24.8 days).
      // Longer plans must be checked in safe intervals or the timer fires
      // immediately and incorrectly clears an active subscription.
      expiryTimer = window.setTimeout(
        checkExpiry,
        Math.min(millisecondsUntilExpiry + 50, MAX_TIMER_DELAY_MS)
      );
    };

    checkExpiry();
    return () => window.clearTimeout(expiryTimer);
  }, [userPlan?.subscribeDateEnd, updateUserPlan]);

  useEffect(() => {
    const userId = user?.userId;
    const accessToken = user?.accessToken;
    if (!userId || !accessToken) return undefined;

    const sessionId = getOrCreateUserSchedulerSessionId();
    const heartbeat = () => {
      heartbeatUserSchedulerSession(accessToken, sessionId).catch((error) => {
        if (error?.status === 401) {
          setUser(null);
          setUserPlan(null);
          sessionStorage.clear();
          return;
        }
        console.warn("Unable to refresh user scheduler session:", error);
      });
    };

    heartbeat();
    const heartbeatTimer = window.setInterval(heartbeat, 30000);
    return () => window.clearInterval(heartbeatTimer);
  }, [user?.userId, user?.accessToken]);

  // ✅ Login
  const login = (userData, planData = null) => {
    try {
      setUser(userData);
      sessionStorage.setItem("userInfo", JSON.stringify(userData));
      updateUserPlan(planData);
    } catch (error) {
      console.error("Login storage error:", error);
    }
  };

  // ✅ Logout
  const logout = () => {
    try {
      const accessToken = user?.accessToken;
      const sessionId = getUserSchedulerSessionId();
      if (accessToken && sessionId) {
        stopUserSchedulerSession(accessToken, sessionId).catch((error) => {
          console.warn("Unable to stop user scheduler session:", error);
        });
      }
      setUser(null);
      setUserPlan(null);
      sessionStorage.clear(); // 🔥 important
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // ✅ Check auth
  const isAuthenticated = () => {
    return !!user?.accessToken;
  };

  // ✅ Safe getter that also rejects an expired cached plan.
  const getUserPlan = () => {
    if (isPlanActive(userPlan)) return userPlan;

    try {
      const storedPlan = sessionStorage.getItem("userPlan");
      const parsedPlan = storedPlan ? JSON.parse(storedPlan) : null;
      return isPlanActive(parsedPlan) ? parsedPlan : null;
    } catch {
      return null;
    }
  };


  const contextValue = {
    user,
    userPlan,
    setUserPlan: updateUserPlan,
    login,
    logout,
    getUserPlan,
    refreshUserPlan,
    isAuthenticated,
    isLoading,
    isPlanLoading,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};


export const useAuth = () => useContext(AuthContext);
